import React, { useEffect, useState, useCallback } from 'react';
import { getAdminAuditLog } from '../services/api';
import { Activity, Filter, Loader2, Shield, User as UserIcon, Package as PackageIcon, AlertCircle } from 'lucide-react';
import Pagination from './Pagination';

const ACTION_LABELS = {
  'user.ban': { label: 'User banned', tone: 'bg-red-100 text-red-700 border-red-200' },
  'user.unban': { label: 'User unbanned', tone: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  'user.premium_on': { label: 'Premium enabled', tone: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  'user.premium_off': { label: 'Premium removed', tone: 'bg-gray-100 text-gray-700 border-gray-200' },
  'user.edit': { label: 'User edited', tone: 'bg-blue-100 text-blue-700 border-blue-200' },
  'user.delete': { label: 'User deleted', tone: 'bg-red-100 text-red-700 border-red-200' },
  'user.bulk_delete': { label: 'Bulk user delete', tone: 'bg-red-100 text-red-700 border-red-200' },
  'delivery.delete': { label: 'Delivery deleted', tone: 'bg-red-100 text-red-700 border-red-200' },
  'delivery.bulk_delete': { label: 'Bulk delivery delete', tone: 'bg-red-100 text-red-700 border-red-200' },
  'delivery.refund_full': { label: 'Full refund', tone: 'bg-orange-100 text-orange-700 border-orange-200' },
  'delivery.refund_partial': { label: 'Partial refund', tone: 'bg-orange-100 text-orange-700 border-orange-200' },
  'delivery.cancel_authorization': { label: 'Auth cancelled', tone: 'bg-amber-100 text-amber-700 border-amber-200' },
  'delivery.force_capture': { label: 'Force capture', tone: 'bg-violet-100 text-violet-700 border-violet-200' },
  'delivery.resend_receipt': { label: 'Receipt resent', tone: 'bg-sky-100 text-sky-700 border-sky-200' },
  'driver.compliance_approve': { label: 'Compliance approved', tone: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  'driver.compliance_reject': { label: 'Compliance rejected', tone: 'bg-red-100 text-red-700 border-red-200' },
};

const FILTER_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'user.ban', label: 'User bans' },
  { value: 'user.unban', label: 'User unbans' },
  { value: 'user.premium_on', label: 'Premium toggles ON' },
  { value: 'user.premium_off', label: 'Premium toggles OFF' },
  { value: 'user.delete', label: 'User deletes' },
  { value: 'delivery.delete', label: 'Delivery deletes' },
  { value: 'delivery.refund_full', label: 'Full refunds' },
  { value: 'delivery.refund_partial', label: 'Partial refunds' },
  { value: 'delivery.cancel_authorization', label: 'Auth cancellations' },
  { value: 'delivery.force_capture', label: 'Force captures' },
  { value: 'driver.compliance_approve', label: 'Compliance approves' },
  { value: 'driver.compliance_reject', label: 'Compliance rejects' },
];

const PAGE_SIZE = 20;

const targetIcon = (t) => {
  if (t === 'delivery') return <PackageIcon className="w-3.5 h-3.5" />;
  if (t === 'user') return <UserIcon className="w-3.5 h-3.5" />;
  return <Shield className="w-3.5 h-3.5" />;
};

const formatRel = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return 'just now';
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
  if (ms < 7 * 86400000) return `${Math.floor(ms / 86400000)}d ago`;
  return new Date(iso).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const AdminAuditLog = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminAuditLog({ action: actionFilter, limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      // silent — keep prior state
    } finally {
      setLoading(false);
    }
  }, [actionFilter, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setPage(0); }, [actionFilter]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="admin-audit-log">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-[#1B5E20]" />
          <h2 className="text-lg font-semibold text-gray-900">Activity Log</h2>
          <span className="text-xs text-gray-500">({total} {total === 1 ? 'entry' : 'entries'})</span>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#2E7D32]"
            data-testid="audit-action-filter"
          >
            {FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-lg py-12 text-center" data-testid="audit-empty">
          <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No admin activity recorded yet.</p>
          <p className="text-xs text-gray-400 mt-1">Actions like bans, refunds, premium toggles and compliance reviews will appear here.</p>
        </div>
      ) : (
        <>
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <ul className="divide-y divide-gray-100">
              {items.map((it) => {
                const meta = ACTION_LABELS[it.action] || { label: it.action, tone: 'bg-gray-100 text-gray-700 border-gray-200' };
                return (
                  <li key={it.id} className="p-3 sm:p-4 hover:bg-gray-50/60" data-testid={`audit-row-${it.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${meta.tone}`}>
                            {meta.label}
                          </span>
                          {it.target_label && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-700 font-medium">
                              {targetIcon(it.target_type)}
                              <span className="truncate max-w-[200px]">{it.target_label}</span>
                            </span>
                          )}
                        </div>
                        {it.summary && (
                          <p className="text-sm text-gray-700 mt-1.5 leading-snug">{it.summary}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400 flex-wrap">
                          <span className="inline-flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            {it.admin_name || it.admin_email || 'Admin'}
                          </span>
                          {it.ip && <span>IP {it.ip}</span>}
                        </div>
                      </div>
                      <span className="text-[11px] text-gray-400 flex-shrink-0" title={new Date(it.created_at).toLocaleString()}>
                        {formatRel(it.created_at)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          {totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={setPage}
              dataTestId="audit-pagination"
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdminAuditLog;
